import { ExpectedError } from "../../expectedError";
import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const PASSWORD_PLACEHOLDER = "__3B_SMTP_PASSWORD_PLACEHOLDER__";
const DEFAULT_PORT = "587";

function hostComponent(host: string | undefined) {
  const value = (host ?? "").trim();
  if (/^\[[0-9a-fA-F:.]+\]$/.test(value)) return value;
  if (!/^[A-Za-z0-9.-]+$/.test(value)) {
    throw new ExpectedError("Invalid SMTP host");
  }
  return value;
}

function portComponent(port: string | undefined) {
  const value = (port || DEFAULT_PORT).trim();
  if (!/^\d+$/.test(value)) throw new ExpectedError("Invalid SMTP port");
  const portNumber = Number(value);
  if (portNumber < 1 || portNumber > 65535) {
    throw new ExpectedError("Invalid SMTP port");
  }
  return value;
}

function tlsComponent(tls: string | undefined) {
  switch (tls) {
    case "implicit":
    case "starttls":
    case "off":
      return tls;
    default:
      return "starttls";
  }
}

function connectionString({
  host,
  port,
  username,
  tls,
}: Record<string, string>) {
  const smtpHost = hostComponent(host);
  const smtpPort = portComponent(port);
  const smtpTls = tlsComponent(tls);
  const scheme = smtpTls === "implicit" ? "smtps" : "smtp";
  const params = new URLSearchParams();
  if (smtpTls === "starttls") params.set("requireTLS", "true");
  if (smtpTls === "off") params.set("ignoreTLS", "true");
  const query = params.size > 0 ? `?${params}` : "";
  const encodedUsername = encodeURIComponent(username ?? "");
  return `${scheme}://${encodedUsername}:${PASSWORD_PLACEHOLDER}@${smtpHost}:${smtpPort}${query}`;
}

export const smtp = defineConnector({
  identifier: "smtp",
  name: "SMTP",
  tagLine: "Send email through any SMTP relay",
  logo,
  tags: ["communication", "custom"],
  protocol: "smtp",
  connectionTest: { protocol: "smtp", connectionString },
  defaultUrlPattern: () => "",
  customizeStepHandledInline: true,
  auth: customAuth("smtp"),
  steps: [
    {
      title: "Enter your SMTP connection",
      items: [
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: "SMTP",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "input",
          label: "Host",
          required: true,
          placeholder: "smtp.example.com",
          target: { kind: "configuration", field: "host" },
        },
        {
          kind: "input",
          label: "Port",
          required: true,
          defaultValue: "587",
          target: { kind: "configuration", field: "port" },
        },
        {
          kind: "input",
          label: "Username",
          required: true,
          target: { kind: "configuration", field: "username" },
        },
        {
          kind: "input",
          label: "Password",
          required: true,
          target: { kind: "secrets", field: "password" },
        },
        {
          kind: "select",
          label: "TLS",
          required: true,
          defaultValue: "starttls",
          target: { kind: "configuration", field: "tls" },
          options: [
            { value: "starttls", label: "STARTTLS (submission, port 587)" },
            { value: "implicit", label: "Implicit TLS (SMTPS, port 465)" },
            { value: "off", label: "Disabled" },
          ],
        },
        {
          kind: "file",
          label: "CA certificate",
          accept: ".pem,.crt,.cer",
          helperText:
            "Upload a PEM CA certificate for private or self-signed SMTP servers.",
          target: { kind: "configuration", field: "tlsCaCertificate" },
          visibleWhen: [
            {
              target: { kind: "configuration", field: "tls" },
              notValue: "off",
            },
          ],
        },
      ],
    },
  ],
  publicEnvVars: ({ host, port, username, tls }) => ({
    SMTP_HOST: hostComponent(host),
    SMTP_PORT: portComponent(port),
    SMTP_USERNAME: username ?? "",
    SMTP_PASSWORD: PASSWORD_PLACEHOLDER,
    SMTP_TLS: tlsComponent(tls),
  }),
  usageHint: () =>
    "Read SMTP_HOST, SMTP_PORT, SMTP_USERNAME, and SMTP_PASSWORD from the environment to connect. SMTP_TLS is one of 'starttls' (upgrade in-band after EHLO), 'implicit' (TLS from the first byte), or 'off' (plaintext).",
});
